import { useCallback } from "react";

import { useGetVoiceDirectoryQuery } from "services/dialstack";

import { isColleagueLine, webrtcDeviceUserId } from "../phone-match.util";

export type Colleague = {
  // Null when the leg came from a colleague's web softphone (which carries
  // their device id, not an extension) and the directory does not list them.
  extension: string | null;
  // Null when the directory has not loaded (or has no such colleague) and the
  // softphone supplied no caller name either.
  name: string | null;
};

export const colleagueLabel = ({ name, extension }: Colleague) => {
  if (name && extension) return `${name} · ext ${extension}`;
  return name ?? (extension ? `Ext ${extension}` : "Colleague");
};

// Resolves a call's peer to the colleague behind it: by extension, or by the
// DialStack user id inside a web softphone's device identity (ENG-2061). The
// directory is only fetched once one of `numbers` is a colleague's line.
export default function useColleagueLookup(
  numbers: (string | null | undefined)[]
) {
  const { data: directory } = useGetVoiceDirectoryQuery(undefined, {
    skip: !numbers.some(isColleagueLine)
  });

  return useCallback(
    (
      number: string | null | undefined,
      fallbackName: string | null = null
    ): Colleague | null => {
      if (!isColleagueLine(number)) return null;
      const line = (number ?? "").trim();
      const userId = webrtcDeviceUserId(line);
      const entry = directory?.find((candidate) =>
        userId ? candidate.user_id === userId : candidate.extension === line
      );
      // The softphone echoes the line back as the "name" when the phone
      // system has none, which is not a name.
      const sdkName = fallbackName?.trim();
      const echoed = !sdkName || sdkName === line || /^\d+$/.test(sdkName);
      return {
        extension: entry?.extension ?? (userId ? null : line),
        name: entry?.name ?? (echoed ? null : sdkName)
      };
    },
    [directory]
  );
}
